import React from 'react';
import { Edit, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { Slider } from '@/components/ui/slider';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { CanonicalEntity } from '@/services/kbService';
import { useEntityTypeOptions } from '@/hooks/useEntityTypeOptions';

export interface QAEditEntityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entity: CanonicalEntity | null;
  entityType: string;
  canonicalValue: string;
  confidenceScore: number;
  notes: string;
  saving: boolean;
  onEntityTypeChange: (type: string) => void;
  onCanonicalValueChange: (value: string) => void;
  onConfidenceScoreChange: (score: number) => void;
  onNotesChange: (notes: string) => void;
  onSave: () => void;
}

export const QAEditEntityModal: React.FC<QAEditEntityModalProps> = ({
  open,
  onOpenChange,
  entity,
  entityType,
  canonicalValue,
  confidenceScore,
  notes,
  saving,
  onEntityTypeChange,
  onCanonicalValueChange,
  onConfidenceScoreChange,
  onNotesChange,
  onSave,
}) => {
  const { entityTypeOptions } = useEntityTypeOptions();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="dark bg-background text-foreground max-w-[95vw] sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit className="h-5 w-5" />
            Edit Entity
          </DialogTitle>
        </DialogHeader>

        {entity && (
          <div className="space-y-4">
            {/* Editable canonical value */}
            <div className="space-y-2">
              <Label>Canonical Value</Label>
              <Input
                value={canonicalValue}
                onChange={(e) => onCanonicalValueChange(e.target.value)}
                placeholder="Entity canonical value"
              />
            </div>

            {/* Aliases display */}
            {entity.aliases && entity.aliases.length > 0 && (
              <div>
                <Label className="text-xs text-muted-foreground">Aliases</Label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {entity.aliases.map((alias, idx) => (
                    <Badge key={`edit-alias-${idx}`} variant="secondary">
                      {alias}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <Separator />

            {/* Entity Type Dropdown */}
            <div className="space-y-2">
              <Label>Entity Type</Label>
              <Select value={entityType} onValueChange={onEntityTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select entity type" />
                </SelectTrigger>
                <SelectContent className="dark">
                  {entityTypeOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Confidence Score Slider */}
            <div className="space-y-2">
              <Label>Confidence Score</Label>
              <div className="flex items-center gap-3">
                <Slider
                  className="flex-1"
                  value={[confidenceScore]}
                  onValueChange={([v]) => onConfidenceScoreChange(v)}
                  min={0}
                  max={100}
                  step={1}
                />
                <span className="text-sm font-medium w-12 text-right shrink-0">{confidenceScore}%</span>
              </div>
            </div>

            {/* Notes Field */}
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                placeholder="Add notes about this entity..."
                rows={3}
                className="resize-none"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={onSave} disabled={saving}>
            {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
